<script>
  import { GroupsRepository, RolesRepository } from "../../repositories/index"
  import { onMount, getContext, createEventDispatcher } from "svelte"
  import { _, callout, user as userStore } from "../../stores/index"
  import { Button, Flex, Badge, FlexItem } from "@gl/design-system"
  import { PoliciesTable, CustomAutocomplete } from "../../components/index"
  const { isAuthorized } = getContext("isAuthorized")
  import { navigate } from "../../utils/index"
  const dispatch = createEventDispatcher()

  export let id
  export let user

  const readOnly = ($userStore.data.id === id && !$userStore.data.isMaster) || !$isAuthorized("user_write")

  let form = {
    groups: [],
    roles: [],
  }

  let groups = []
  let roles = []

  let autocompleteRoles = []
  let autocompleteGroups = []

  let userPolicies = []
  let groupsPolicies = []
  let rolesPolicies = []

  let loading = {}

  async function getGroups() {
    try {
      loading.groups = true

      let groupsList = await GroupsRepository.getGroups({ missingPolicies: true })
      groupsList = groupsList.list

      const groupsRoles = await Promise.all(groupsList.map((g) => GroupsRepository.getGroupRoles(g.id)))

      groups = groupsList.map((group, index) => (group = { ...group, roles: groupsRoles[index].list }))

      if (user.groups.length) {
        form.groups = user.groups.map(
          (group) =>
            (group = {
              label: group.name,
              value: group.id,
              disabled: !!groupsList.find((g) => g.id === group.id).missingPolicies,
            })
        )
      }

      autocompleteGroups = groupsList.map(
        (group) => (group = { label: group.name, value: group.id, disabled: !!group.missingPolicies })
      )
    } finally {
      loading.groups = false
    }
  }

  async function getRoles() {
    loading.roles = true
    try {
      let rolesList = await RolesRepository.getRoles({ missingPolicies: true })
      rolesList = rolesList.list

      const rolePolicies = await Promise.all(rolesList.map((r) => RolesRepository.getRolePolicies(r.id)))

      roles = rolesList.map((role, index) => (role = { ...role, policies: rolePolicies[index].list }))

      if (user.roles.length) {
        form.roles = user.roles.map(
          (role) =>
            (role = {
              label: role.name,
              value: role.id,
              disabled: !!rolesList.find((r) => r.id === role.id).missingPolicies,
            })
        )
      }

      autocompleteRoles = rolesList.map(
        (role) => (role = { label: role.name, value: role.id, disabled: !!role.missingPolicies })
      )
    } finally {
      loading.roles = false
    }
  }

  function generatePoliciesFromRolesAndGroups() {
    loading.policies = true
    const selectedGroups = groups.filter((g) => form.groups.some((fg) => fg.value === g.id))
    const selectedGroupsRoles = selectedGroups.flatMap((g) => g.roles)
    groupsPolicies = roles.filter((r) => selectedGroupsRoles.some((ur) => r.id === ur)).flatMap((r) => r.policies)

    rolesPolicies = roles.filter((r) => form.roles.some((ur) => r.id === ur.value)).flatMap((r) => r.policies)

    let selectedPolicies = [...groupsPolicies, ...rolesPolicies]
    userPolicies = [...new Set(selectedPolicies)]

    loading.policies = false
  }

  async function handleSave() {
    let changes = { roles: { add: [], remove: [] }, groups: { add: [], remove: [] } }

    for (let entity of ["roles", "groups"]) {
      // roles/groups to remove
      if (user[entity].length > 0) {
        changes[entity].remove = user[entity].filter((en) => !form[entity].some((el) => el.value === en.id))
      }

      changes[entity].add = user[entity]
        ? form[entity].filter((el) => !user[entity].some((en) => en.id === el.value))
        : form[entity]
    }

    if (Object.values(changes).every((entity) => Object.values(entity).every((action) => action.length === 0))) {
      callout.set({
        msg: $_("no.changes.callout"),
        status: "warning",
        timeout: true,
      })
      loading.save = false
      dispatch("save")
      navigate("/users")
      return
    }

    try {
      loading.save = true

      // add/remove groups
      if (changes.groups.remove.length > 0)
        await Promise.all(changes.groups.remove.map((g) => GroupsRepository.deleteGroupUser(g.id, { users: [id] })))

      if (changes.groups.add.length > 0) {
        await Promise.all(changes.groups.add.map((g) => GroupsRepository.addGroupUser(g.value, { users: [id] })))
      }

      // add/remove roles
      if (changes.roles.add.length > 0)
        await Promise.all(changes.roles.add.map((role) => RolesRepository.addRoleUser(role.value, { users: [id] })))

      if (changes.roles.remove.length > 0)
        await Promise.all(changes.roles.remove.map((r) => RolesRepository.deleteRoleUser(r.id, { users: [id] })))
      callout.set({
        msg: $_("policies.save.callout"),
        status: "success",
        timeout: true,
      })
    } finally {
      loading.save = false
      dispatch("save")
      navigate("/users")
    }
  }

  async function fetchAll() {
    await Promise.all([getRoles()]).then(() => {
      getGroups().then(() => {
        generatePoliciesFromRolesAndGroups()
      })
    })
  }

  const handleReset = () => {
    form.groups = []
    form.roles = []
    fetchAll()
  }

  $: if (form) generatePoliciesFromRolesAndGroups()
  onMount(() => {
    fetchAll()
  })
</script>

{#if user.data && user.data.isMaster}
  <div class="mb-4">
    <Badge class="mt-2" status="success" label={$_("users.master")} />
  </div>
{/if}

{#if loading.groups === false && loading.roles === false && loading.policies === false}
  <Flex>
    <div class="flex-grow-1 mr-4 autocomplete-groups">
      <b>Groups</b>
      <div class="my-3 mb-5">
        <CustomAutocomplete options={autocompleteGroups} bind:value={form.groups} {readOnly} tagColor="primary" />
      </div>
    </div>

    <div class="flex-grow-1 autocomplete-roles">
      <b>Roles</b>
      <div class="my-3 mb-5">
        <CustomAutocomplete options={autocompleteRoles} bind:value={form.roles} {readOnly} tagColor="warning" />
      </div>
    </div>
  </Flex>

  <PoliciesTable subPolicies={userPolicies} disabled={true} {groupsPolicies} {rolesPolicies} />
  {#if $isAuthorized("user_write")}
    <div class="footer-buttons py-6">
      <Button on:click={handleReset} outlined>{$_("common.reset")}</Button>
      <FlexItem pushEnd>
        <Button on:click={handleSave} class="mr-3" loading={loading.save}>{$_("buttons.save")}</Button>
        <Button on:click={() => navigate("/users")} outlined>{$_("buttons.cancel")}</Button>
      </FlexItem>
    </div>
  {/if}
{/if}

<style>
  .autocomplete-groups :global(.gl-badge.variant-tag),
  .autocomplete-roles :global(.gl-badge.variant-tag) {
    color: white;
  }

  .autocomplete-groups :global(.gl-icon),
  .autocomplete-roles :global(.gl-icon) {
    color: white;
  }
  .autocomplete-groups :global(.gl-badge.variant-tag) {
    background-color: var(--gl-primary);
  }

  .autocomplete-roles :global(.gl-badge.variant-tag) {
    background-color: var(--gl-warning);
  }
</style>
